import i18n from '../i18n/i18n';

/**
 * Simple Sidenav Component
 * Handles side navigation with mobile tabs and focus management
 *
 * @example
 * const sidenav = new Sidenav();
 *
 * @version 4.4.0 - Using centralized i18n module
 */
class Sidenav {
  /**
   * Default configuration object
   * @static
   * @readonly
   * @returns {Object} Default configuration
   */
  static get DEFAULT_CONFIG() {
    return {
      selectors: {
        navLinks: 'a[href^="#toc-"]',
        mobileActionTogglers: '.mobile-action-togglers button',
        articleTabs: '.article-tab',
      },
      focusableSelectors: [
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'a[href]',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]',
      ],
      debug: false,
    };
  }

  /**
   * Creates a new Sidenav instance
   * @param {Object} [config={}] - Configuration object to override defaults
   */
  constructor(config = {}) {
    this.config = Object.assign({}, Sidenav.DEFAULT_CONFIG, config);

    // Global debug switch (window.__SP_DEBUG or localStorage 'sp-debug' = '1')
    try {
      const globalDebug =
        (typeof window !== 'undefined' && (window.__SP_DEBUG || localStorage.getItem('sp-debug') === '1')) || false;
      if (typeof config.debug !== 'boolean') {
        this.config.debug = Boolean(globalDebug || this.config.debug);
      }
    } catch (_) {
      // ignore storage errors
    }

    // Check DOM first (no i18n calls yet)
    const navLinks = document.querySelectorAll(this.config.selectors.navLinks);
    const mobileTogglers = document.querySelectorAll(this.config.selectors.mobileActionTogglers);
    const articleTabs = document.querySelectorAll(this.config.selectors.articleTabs);

    const hasSidemenu = mobileTogglers.length > 0 || articleTabs.length > 0 || navLinks.length > 0;

    if (!hasSidemenu) {
      this.log('No side menu DOM found. Skipping init.');
      return;
    }

    // Initialize i18n if not already initialized
    /*
      if (!i18n.getLanguage() || i18n.getLanguage() === 'fi') {
        i18n.init();
      }
    */

    this.elements = this.cacheElements();

    // Cache for mobile menu state
    this._mobileMenuOpenCache = undefined;

    // Track last focused toggler for Escape key handling
    this._lastFocusedToggler = null;

    // Track last activation modality
    this._lastActivationWasPointer = false;

    // Bound handlers so removeEventListener works
    this._handlers = {
      navClick: this.handleNavClick.bind(this),
      keydown: this.handleKeydown.bind(this),
      escape: this.handleEscapeKey.bind(this),
    };

    // Per-toggler handlers to properly remove later
    this._togglerHandlers = new Map();

    // Pre-join selectors for reuse
    this._focusableSelectorString = this.config.focusableSelectors.join(', ');

    this.init();
  }

  /**
   * Get current language labels from i18n
   * @private
   * @returns {Object} Language-specific labels
   */
  getLabels() {
    // Safe fallbacks if i18n not ready
    const defaults = {
      openTab: 'Open',
      closeTab: 'Close',
      tableOfContents: 'Table of contents',
      share: 'Share',
      menu: 'Menu',
      menuOpened: 'opened',
      menuClosed: 'closed',
      menuClosedFocusReturned: 'Menu closed, focus returned',
      navigatedTo: 'Navigated to',
    };
    const ns = i18n && typeof i18n.getNamespace === 'function' ? i18n.getNamespace('sidemenu') : null;
    return Object.assign({}, defaults, ns || {});
  }

  /**
   * Cache DOM elements for performance
   * @private
   * @returns {Object} Object containing cached DOM elements
   */
  cacheElements() {
    const { selectors } = this.config;

    const cached = {
      navLinks: Array.from(document.querySelectorAll(selectors.navLinks)),
      togglers: Array.from(document.querySelectorAll(selectors.mobileActionTogglers)),
      tabs: Array.from(document.querySelectorAll(selectors.articleTabs)),
      liveRegion: null,
    };
    this.log('cacheElements', {
      navLinks: cached.navLinks.length,
      togglers: cached.togglers.length,
      tabs: cached.tabs.length,
    });
    return cached;
  }

  /**
   * Mobile menu state check with caching
   * @returns {boolean} True if any mobile tab is currently open
   */
  get isMobileMenuOpen() {
    if (this._mobileMenuOpenCache === undefined) {
      this._mobileMenuOpenCache = this.elements.tabs.some(
        tab => tab.classList.contains('active') && !tab.classList.contains('d-none')
      );
    }
    return this._mobileMenuOpenCache;
  }

  /**
   * Clear mobile menu open state cache
   * @private
   */
  clearMobileMenuCache() {
    this._mobileMenuOpenCache = undefined;
  }

  /**
   * Initialize the sidenav component
   * @private
   */
  init() {
    this.log('init start', { lang: i18n && i18n.getLanguage ? i18n.getLanguage() : 'unknown' });

    // Create live region for screen reader announcements
    this.createLiveRegion();

    // Set up accessibility attributes
    this.setupAccessibilityAttributes();

    // Add event listener to close menu when skip to content is activated
    const skipLinks = document.querySelectorAll('.jump-to-content-link');
    skipLinks.forEach(link => {
      link.addEventListener('click', () => {
        this.closeAllMobileTabs();
      });
    });

    // Navigation click listeners (stable reference)
    this.elements.navLinks.forEach(link => {
      link.addEventListener('click', this._handlers.navClick);
    });
    this.log('navLinks listeners bound', this.elements.navLinks.length);

    // Keyboard listeners (stable references)
    document.addEventListener('keydown', this._handlers.keydown);
    document.addEventListener('keydown', this._handlers.escape);

    // Mobile toggler support
    this.initMobileActionTogglers();

    this.log('init done');
  }

  /**
   * Create ARIA live region for announcements
   * @private
   */
  createLiveRegion() {
    //main-nav.js already creates a live region, so check before creating another

    if (!this.elements.liveRegion && typeof document !== 'undefined' && document.body) {
      const liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.setAttribute('class', 'sr-only');
      liveRegion.setAttribute('lang', i18n.getLanguage());
      liveRegion.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
      document.body.appendChild(liveRegion);
      this.elements.liveRegion = liveRegion;
    }
  }

  /**
   * Announce message to screen readers
   * @private
   * @param {string} message - Message to announce
   */
  announce(message) {
    if (this.elements.liveRegion) {
      // Clear first to ensure announcement is read even if same message
      this.elements.liveRegion.textContent = '';
      setTimeout(() => {
        this.elements.liveRegion.textContent = message;
      }, 100);
    }
  }

  /**
   * Setup accessibility attributes for tabs and togglers
   * @private
   */
  setupAccessibilityAttributes() {
    const { tabs, togglers } = this.elements;

    // Setup tabs with proper roles and labels
    tabs.forEach(tab => {
      if (!tab.hasAttribute('role')) {
        tab.setAttribute('role', 'region');
      }

      // Generate ID if not present
      if (!tab.id) {
        const tabClass = Array.from(tab.classList).find(c => c !== 'article-tab' && c !== 'active' && c !== 'd-none');
        tab.id = `tab-${tabClass || Math.random().toString(36).substr(2, 9)}`;
      }

      // Set initial aria-hidden
      const isHidden = tab.classList.contains('d-none');
      tab.setAttribute('aria-hidden', String(isHidden));

      // Add aria-labelledby if header exists
      const heading = tab.querySelector('h2, h3, h4, [role="heading"]');
      if (heading && !heading.id) {
        heading.id = `${tab.id}-heading`;
        tab.setAttribute('aria-labelledby', heading.id);
      }
    });

    // Setup togglers with proper labels and controls
    togglers.forEach(toggler => {
      const tabType = toggler.getAttribute('data-tab');
      if (!tabType) return;

      const matchingTab = tabs.find(tab => tab.classList.contains(tabType));
      if (matchingTab) {
        // Set aria-controls
        toggler.setAttribute('aria-controls', matchingTab.id);

        // Set aria-label if not present
        if (!toggler.hasAttribute('aria-label')) {
          const label = this.getTogglerLabel(tabType);
          toggler.setAttribute('aria-label', label);
        }

        // Ensure aria-expanded exists
        if (!toggler.hasAttribute('aria-expanded')) {
          toggler.setAttribute('aria-expanded', 'false');
        }
      }
    });
  }

  /**
   * Get appropriate label for toggler
   * @private
   * @param {string} tabType - Type of tab
   * @returns {string} Accessible label
   */
  getTogglerLabel(tabType) {
    const labels = this.getLabels();

    // Map tab types to user-friendly names
    const tabNames = {
      'table-of-contents': labels.tableOfContents,
      'share': labels.share,
    };

    const tabName = tabNames[tabType] || tabType;
    return `${labels.openTab} ${tabName}`;
  }

  /**
   * Update toggler label based on state
   * @private
   * @param {HTMLElement} toggler - Toggler element
   * @param {boolean} isExpanded - Whether the tab is expanded
   */
  updateTogglerLabel(toggler, isExpanded) {
    const tabType = toggler.getAttribute('data-tab');
    if (!tabType) return;

    const labels = this.getLabels();
    const tabNames = {
      'table-of-contents': labels.tableOfContents,
      'share': labels.share,
    };

    const tabName = tabNames[tabType] || tabType;
    const action = isExpanded ? labels.closeTab : labels.openTab;
    toggler.setAttribute('aria-label', `${action} ${tabName}`);
  }

  /**
   * Set ARIA state with minimal DOM updates
   * @private
   * @param {HTMLElement} element - The element to update
   * @param {boolean} isExpanded - Whether the element is expanded
   */
  setAriaExpanded(element, isExpanded) {
    const currentState = element.getAttribute('aria-expanded') === 'true';
    if (currentState !== isExpanded) {
      element.setAttribute('aria-expanded', String(isExpanded));
    }
  }

  /**
   * Set toggler state with minimal DOM updates
   * @private
   * @param {HTMLElement} toggler - The toggler element to update
   * @param {boolean} isActive - Whether the toggler should be active
   */
  setTogglerState(toggler, isActive) {
    if (toggler.getAttribute('aria-expanded') !== String(isActive)) {
      toggler.classList.toggle('active', isActive);
      toggler.setAttribute('aria-expanded', String(isActive));
      this.updateTogglerLabel(toggler, isActive);
    }
  }

  /**
   * Set tab state with minimal DOM updates
   * @private
   * @param {HTMLElement} tab - The tab element to update
   * @param {boolean} isActive - Whether the tab should be active
   */
  setTabState(tab, isActive) {
    const needsUpdate =
      tab.classList.contains('active') !== isActive ||
      tab.classList.contains('d-none') === isActive;

    if (needsUpdate) {
      tab.classList.toggle('active', isActive);
      tab.classList.toggle('d-none', !isActive);
      tab.setAttribute('aria-hidden', String(!isActive));
      this.clearMobileMenuCache();

      // Announce state change to screen readers
      if (isActive) {
        const labels = this.getLabels();
        const tabLabel = tab.getAttribute('aria-label') ||
                        (tab.querySelector('h2, h3, h4') || {}).textContent ||
                        labels.menu;
        this.announce(`${tabLabel} ${labels.menuOpened}`);
      }
    }
  }

  /**
   * Initialize mobile action togglers
   * @private
   */
  initMobileActionTogglers() {
    const { togglers, tabs } = this.elements;

    if (!togglers.length || !tabs.length) {
      this.log('No mobile togglers or tabs found');
      return;
    }

    // Ensure previous handlers are removed and re-bound
    togglers.forEach(toggler => {
      const prev = this._togglerHandlers.get(toggler);
      if (prev) {
        toggler.removeEventListener('click', prev);
      }

      const handler = (e) => {
        e.preventDefault();
        // detail > 0 indicates pointer-initiated click; detail === 0 indicates keyboard/AT
        this._lastActivationWasPointer = e && typeof e.detail === 'number' ? e.detail > 0 : true;
        this._lastFocusedToggler = toggler;
        this.handleMobileActionToggle(toggler);
      };

      this._togglerHandlers.set(toggler, handler);
      toggler.addEventListener('click', handler);
    });

    this.log('mobile togglers bound', togglers.length);
  }

  /**
   * Close main navigation if open
   * @private
   * @returns {void}
   */
  closeMainNavIfOpen() {
    const mobileMainNavWrap = document.getElementById('mobileMainNavWrap');
    const body = document.body;

    if (mobileMainNavWrap && mobileMainNavWrap.style.display === 'block') {
      this.log('Closing mobile main nav before opening sidemenu');
      // Close main nav
      mobileMainNavWrap.style.display = 'none';
      body.classList.remove('mobile-main-nav-visible');

      // Update toggle buttons
      const toggles = Array.from(document.querySelectorAll('[data-mobile-main-nav-toggle]'));
      toggles.forEach(function(toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.classList.remove('selected');
      });
    }
  }

  /**
   * Handle mobile action toggle click
   * @private
   * @param {HTMLElement} clickedToggler - The toggler that was clicked
   */
  handleMobileActionToggle(clickedToggler) {
    const { togglers, tabs } = this.elements;
    const isCurrentlyExpanded = clickedToggler.getAttribute('aria-expanded') === 'true';
    const targetClass = clickedToggler.getAttribute('data-tab');
    this.log('handleMobileActionToggle', { targetClass, isCurrentlyExpanded });

    // If opening a tab, close main nav first
    if (!isCurrentlyExpanded) {
      this.closeMainNavIfOpen();
    }

    // Update all togglers
    togglers.forEach(toggler => {
      const shouldBeActive = toggler === clickedToggler && !isCurrentlyExpanded;
      this.setTogglerState(toggler, shouldBeActive);
    });

    // Update all tabs
    tabs.forEach(tab => {
      const shouldBeActive = !isCurrentlyExpanded && tab.classList.contains(targetClass);
      this.setTabState(tab, shouldBeActive);
    });

    // Focus management for newly opened tab
    if (!isCurrentlyExpanded) {
      const targetTab = tabs.find(tab => tab.classList.contains(targetClass));
      if (targetTab) {
        //if mouse click, focus on second element (first is skip to content link)


        this.focusFirstElementInTab(targetTab);
      }
    } else {
      // Tab was closed, announce it
      const labels = this.getLabels();
      this.announce(labels.menuClosed);
    }
  }

  /**
   * Focus first focusable element in tab
   * @private
   * @param {HTMLElement} tabElement - The tab element to focus within
   */
  focusFirstElementInTab(tabElement) {
    const focusables = Array.from(tabElement.querySelectorAll(this._focusableSelectorString))
      .filter(el => el.offsetParent !== null);
    if (!focusables.length) return;

    // If pointer activation, skip the first focusable (assumed skip link) and use the next
    const target = this._lastActivationWasPointer && focusables.length > 1
      ? focusables[1]
      : focusables[0];

    setTimeout(() => {
      target.focus();
    }, 50);
  }

  /**
   * Get focusable elements in mobile tab (for focus trap)
   * @private
   * @param {HTMLElement} tab - The tab element
   * @returns {Array<HTMLElement>} Array of focusable elements
   */
  getFocusableElements(tab) {
    // Get all focusable elements inside the tab
    const tabFocusables = Array.from(tab.querySelectorAll(this._focusableSelectorString));

    // Get visible togglers (close buttons)
    const visibleTogglers = this.elements.togglers.filter(
      toggler => toggler.offsetParent !== null
    );

    // Togglers first, then tab content - this creates the correct tab order
    return visibleTogglers.concat(tabFocusables);
  }

  /**
   * Check if element is within the focus trap container
   * @private
   * @param {HTMLElement} element - Element to check
   * @param {HTMLElement} container - Container element
   * @param {Array<HTMLElement>} togglers - Array of toggler elements
   * @returns {boolean} True if element is within focus trap area
   */
  isWithinFocusTrap(element, container, togglers) {
    if (!element) return false;

    // Check if element is one of the togglers
    if (togglers.includes(element)) return true;

    // Check if element is inside the container
    return container.contains(element);
  }

  /**
   * Handle keydown for focus trap
   * @private
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeydown(event) {
    if (event.key !== 'Tab') return;

    const openTab = this.elements.tabs.find(
      tab => tab.classList.contains('active') && !tab.classList.contains('d-none')
    );

    if (!openTab) return;

    const focusables = this.getFocusableElements(openTab);
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const activeElement = document.activeElement;

    // Check if current focus is within our trap area
    const isWithinTrap = this.isWithinFocusTrap(activeElement, openTab, this.elements.togglers);

    // If focus is outside the trap, bring it back to the first element
    if (!isWithinTrap) {
      event.preventDefault();
      first.focus();
      return;
    }

    // Handle tab wrapping at boundaries
    if (event.shiftKey) {
      // Shift+Tab at first element -> go to last
      if (activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      // Tab at last element -> go to first
      if (activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /**
   * Handle Escape key to close mobile tabs
   * @private
   * @param {KeyboardEvent} event - The keydown event
   */
  handleEscapeKey(event) {
    if (event.key === 'Escape') {
      const wasOpen = this.isMobileMenuOpen;
      this.log('Escape pressed', { wasOpen });
      // Close all mobile tabs
      this.closeAllMobileTabs();

      // Return focus to the last toggler that opened a tab
      if (wasOpen && this._lastFocusedToggler) {
        this._lastFocusedToggler.focus();
        const labels = this.getLabels();
        this.announce(labels.menuClosedFocusReturned);
      }
    }
  }

  /**
   * Close all mobile tabs
   * @private
   */
  closeAllMobileTabs() {
    const { togglers, tabs } = this.elements;
    tabs.forEach(tab => this.setTabState(tab, false));
    togglers.forEach(toggler => this.setTogglerState(toggler, false));
    this.log('All mobile tabs closed');
  }

  /**
   * Handle navigation link clicks
   * @private
   * @param {MouseEvent} event - The click event
   */
  handleNavClick(event) {
    event.preventDefault();

    const href = event.currentTarget.getAttribute('href');
    const targetId = href ? href.substring(1) : null;

    if (!targetId) return;

    const targetElement = document.getElementById(targetId);
    if (!targetElement) return;

    // Smooth scroll to target
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });

    // Set focus for accessibility
    this.setFocusToElement(targetElement);

    // Announce navigation
    const labels = this.getLabels();
    const linkText = event.currentTarget.textContent.trim();
    this.announce(`${labels.navigatedTo} ${linkText}`);

    // Close mobile tab if clicking from it
    const mobileTab = event.currentTarget.closest('.article-tab');
    if (mobileTab) {
      this.closeMobileTabAndFocusToggler(mobileTab);
    }
  }

  /**
   * Close mobile tab and focus its toggler
   * @private
   * @param {HTMLElement} mobileTab - The mobile tab to close
   */
  closeMobileTabAndFocusToggler(mobileTab) {
    this.setTabState(mobileTab, false);

    const tabClasses = Array.from(mobileTab.classList);
    const toggler = this.elements.togglers.find(t => {
      const tabType = t.getAttribute('data-tab');
      return tabType && tabClasses.includes(tabType);
    });

    if (toggler) {
      this.setTogglerState(toggler, false);
      setTimeout(() => {
        toggler.focus();
      }, 50);
    }
  }

  /**
   * Set focus to element with proper cleanup
   * @private
   * @param {HTMLElement} element - The element to focus
   */
  setFocusToElement(element) {
    if (!element) return;

    const hasTabIndex = element.hasAttribute('tabindex');

    if (!hasTabIndex) {
      element.setAttribute('tabindex', '-1');
    }

    element.focus({ preventScroll: true });
    element.style.outline = 'none';

    if (!hasTabIndex) {
      setTimeout(function() {
        element.removeAttribute('tabindex');
      }, 100);
    }
  }

  /**
   * Destroy the Sidenav instance and clean up
   * @public
   */
  destroy() {
    // Clean listeners safely
    this.cleanupEventListeners();

    // Remove live region
    if (this.elements && this.elements.liveRegion && this.elements.liveRegion.parentNode) {
      this.elements.liveRegion.parentNode.removeChild(this.elements.liveRegion);
    }

    // Clear references
    this.elements = null;
    this.config = null;
    this._lastFocusedToggler = null;
    this._togglerHandlers = null;
    this._handlers = null;

    this.log('destroyed');
  }

  /**
   * Refresh component by re-caching elements
   * @public
   */
  refresh() {
    // Unbind old listeners then re-cache and re-bind
    this.cleanupEventListeners();
    this.elements = this.cacheElements();
    this.setupAccessibilityAttributes();

    // Re-bind listeners
    this.elements.navLinks.forEach(link => {
      link.addEventListener('click', this._handlers.navClick);
    });
    document.addEventListener('keydown', this._handlers.keydown);
    document.addEventListener('keydown', this._handlers.escape);
    this.initMobileActionTogglers();

    this.log('refreshed');
  }

  cleanupEventListeners() {
    try {
      if (this.elements && this.elements.navLinks) {
        this.elements.navLinks.forEach(link => {
          link.removeEventListener('click', this._handlers && this._handlers.navClick);
        });
      }
      document.removeEventListener('keydown', this._handlers && this._handlers.keydown);
      document.removeEventListener('keydown', this._handlers && this._handlers.escape);

      if (this._togglerHandlers && this._togglerHandlers.size) {
        this._togglerHandlers.forEach((handler, el) => {
          el.removeEventListener('click', handler);
        });
        this._togglerHandlers.clear();
      }
    } catch (e) {
      this.log('cleanupEventListeners error', e);
    }
  }

  log() {
    if (this.config && this.config.debug) {
      const ts = new Date().toISOString();
      const args = Array.prototype.slice.call(arguments);
      console.log.apply(console, ['[Sidenav]', ts].concat(args));
    }
  }
}

/**
 * Initialize Sidenav
 */
export default {
  init() {
    new Sidenav();
  },
};
